import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/api";
import ReportCharts from "@/components/ReportCharts";
import { ArrowLeft, ArrowsLeftRight } from "@phosphor-icons/react";

function stripLines(text) {
  return (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
}

export default function Compare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const a = params.get("a"), b = params.get("b");
  const [ra, setRa] = useState(null);
  const [rb, setRb] = useState(null);

  useEffect(() => {
    if (a) api.get(`/reports/${a}`).then((r) => setRa(r.data)).catch(() => setRa(false));
    if (b) api.get(`/reports/${b}`).then((r) => setRb(r.data)).catch(() => setRb(false));
  }, [a, b]);

  if (ra === false || rb === false) return <div className="text-red-600">One of the reports could not be loaded.</div>;
  if (!ra || !rb) return <div className="text-slate-500">Loading comparison…</div>;

  const ids = Array.from(new Set([...ra.sections.map((s) => s.id), ...rb.sections.map((s) => s.id)]));
  const secA = Object.fromEntries(ra.sections.map((s) => [s.id, s]));
  const secB = Object.fromEntries(rb.sections.map((s) => [s.id, s]));

  const Col = ({ r, side }) => (
    <div className="card p-5" data-testid={`compare-col-${side}`}>
      <div className="text-xs font-semibold text-[#b45309]">{side === "a" ? "Report A" : "Report B"}</div>
      <div className="text-slate-900 font-semibold mt-1 leading-snug">{r.title}</div>
      <div className="text-xs text-slate-500 mt-1">Issue {r.issue_no || "—"} · {r.issue_date || "—"} · v{r.version || 1}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="compare-page">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/history")} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm" data-testid="compare-back">
          <ArrowLeft size={18} /> History
        </button>
        <div className="flex items-center gap-2 text-slate-700 text-sm font-medium"><ArrowsLeftRight size={18} className="text-[#004182]" /> Report Comparison</div>
      </div>

      <div className="grid grid-cols-2 gap-4"><Col r={ra} side="a" /><Col r={rb} side="b" /></div>

      {(ra.charts?.length > 0 || rb.charts?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4"><div className="text-sm text-slate-700 font-medium mb-3">A · Trends</div><ReportCharts charts={ra.charts} light /></div>
          <div className="card p-4"><div className="text-sm text-slate-700 font-medium mb-3">B · Trends</div><ReportCharts charts={rb.charts} light /></div>
        </div>
      )}

      <div className="space-y-4">
        {ids.map((sid) => {
          const sa = secA[sid], sb = secB[sid];
          return (
            <div key={sid} data-testid={`compare-section-${sid}`} className="card overflow-hidden">
              <div className="px-5 py-2.5 bg-slate-50 text-slate-900 font-semibold text-sm border-b border-slate-100">{sa?.title || sb?.title}</div>
              <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                <div className="p-4 text-sm text-slate-700 space-y-1">
                  {stripLines(sa?.content).map((l, i) => <div key={i} className="leading-relaxed">{l}</div>)}
                  {!sa && <span className="text-slate-400 italic">Not present</span>}
                </div>
                <div className="p-4 text-sm text-slate-700 space-y-1">
                  {stripLines(sb?.content).map((l, i) => <div key={i} className="leading-relaxed">{l}</div>)}
                  {!sb && <span className="text-slate-400 italic">Not present</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
